import type {
  BasicData,
  FluidProperties,
  FluidPropertiesContext
} from "@/libs/calculations/fluidProperties/d";
import type { TransformerTapData } from "@/libs/calculations/surfaceDrive";
import type { StageCalculations } from "@/libs/calculations/pumps/pumpData";
import type { ComputedRef, WritableComputedRef } from "vue";
import type {
  XrefCables,
  XrefHousing,
  XrefHousings,
  XrefIntakes,
  XrefPump,
  XrefPumps,
  XrefSeals,
  XrefYToolBypassTubings,
  XrefYToolEquipmentTubings
} from "@/composables/forms/formTypes";
import type { CableData } from "@/libs/calculations/cables";
import type { Maybe } from "graphql/jsutils/Maybe";
import type { Deviation_Survey_Directional_Dataset_Enum } from "gql/api";
import type { DeepMaybeRef } from "@vueuse/core";
import type { DestructuredScenario } from "../useDestructureScenario";
import type { PipeFrictionProperties } from "@/libs/calculations/tubingFrictionLoss";
import type { FlowRates } from "@/libs/calculations/productionRates";
import type { LagrangeInterpolationMapWithGroupings } from "@/composables/scenarioCalculations/lagrangeInterpolation";
import type { FluidPropertiesPipelineResults } from "./fluidPropertiesPipeline";
import type { HPSFluidPropertiesPipelineResults } from "./hpsFluidPropertiesPipeline";
import type { SealData } from "@/libs/calculations/seals";
import type { MotorVoltageCorrections } from "@/libs/calculations/motors";

export type DeviationSurveyEntryRows = {
  data: number[][];
  surveyType: Deviation_Survey_Directional_Dataset_Enum | null | undefined;
};

export enum EquipmentCalculatorSectionKey {
  PUMP = "pump",
  MOTOR = "motor",
  INTAKE_CONDITIONS = "intakeConditions",
  CABLE = "cables",
  FLUID_PROPERTIES = "fluidProperties",
  GAS_PROPERTIES = "gasProperties",
  WELL_COMPLETION = "wellCompletion",
  WELL_TEST = "wellTest",
  SEAL = "seal",
  SEAL_MOTOR_OIL = "sealMotorOil",
  MLE = "mle",
  SURFACE_DRIVE = "surfaceDrive",
  OPERATING_PARAMETERS = "operatingParameters",
  THRUST_CHAMBER = "thrustChamber",
  HPS_MOTOR = "hpsMotor"
}

export type EquipmentLengths = {
  totalCableLengthFt: ComputedRef<number>;
  totalCableOnlyLengthFt: ComputedRef<number>;
  motorLengthFt: ComputedRef<number>;
  sensorLengthFt: ComputedRef<number>;
  intakeLengthFt: ComputedRef<number>;
  sealLengthFt: ComputedRef<number>;
  pumpLengthFt: ComputedRef<number>;
  lengthForMleFt: ComputedRef<number>;
  recommendedMleLengthFt: ComputedRef<number>;
  depthToReachMLEPlugFt: ComputedRef<number>;
  recommendedTotalCableLengthFt: ComputedRef<number>;
  cableSurfaceLengthFt: ComputedRef<number>;
  shroudLengthFt: ComputedRef<number>;
  equipmentLengthSAGDFt: ComputedRef<number>;
  tubingLengthFt: ComputedRef<number>;
  datumPointFt: WritableComputedRef<number>;
  datumPointTVD: ComputedRef<number>;
  bottomOfYToolDepthFt: ComputedRef<number>;
  boltOnDischargeDepthFt: ComputedRef<number>;
  boltOnDischargeLengthFt: ComputedRef<number>;
  bottomOfDischargeDepthTVDFt: ComputedRef<number>;
  completePumpDepthFt: ComputedRef<number>;
  bottomOfPumpsDepthFt: ComputedRef<number>;
  bottomOfPumpsTVDFt: ComputedRef<number>;
  bottomOfEquipmentDepthFt: ComputedRef<number>;
  bottomOfEquipmentTVDFt: ComputedRef<number>;
  bottomOfMotorDepthFt: ComputedRef<number>;
  bottomOfMotorTVDFt: ComputedRef<number>;
  bottomOfSealsDepthFt: ComputedRef<number>;
  bottomOfSealsTVDFt: ComputedRef<number>;
  bottomOfIntakesDepthFt: ComputedRef<number>;
  bottomOfIntakesTVDFt: ComputedRef<number>;
  equipmentLengthFt: ComputedRef<number>;
  totalCasingLengthFt: ComputedRef<number>;
  yToolEquipmentTubingLengthFt: ComputedRef<number>;
  yToolBypassTubingLengthFt: ComputedRef<number>;
  yToolBypassTubingDepthFt: ComputedRef<number>;
};

export type EquipmentWeights = {
  topCasingWeightLbs: ComputedRef<number>;
  bottomCasingWeightLbs: ComputedRef<number>;
  totalCasingWeightLbs: ComputedRef<number>;
  motorWeightLbs: ComputedRef<number>;
  boltOnDischargeWeightLbs: ComputedRef<number>;
  sensorWeightLbs: ComputedRef<number>;
  intakeWeightLbs: ComputedRef<number>;
  sealWeightLbs: ComputedRef<number>;
  pumpWeightLbs: ComputedRef<number>;
  cableWeightLbs: ComputedRef<number>;
  mleWeightLbs: ComputedRef<number>;
  tubingWeightLbs: ComputedRef<number>;
  topTubingWeightLbs: ComputedRef<number>;
  bottomTubingWeightLbs: ComputedRef<number>;
  equipmentWeightLbs: ComputedRef<number>;
  totalStringWeightLbs: ComputedRef<number>;
};

export type MotorData = {
  internal_temperature: number;
  skin_temperature: number;
  flow_velocity_around_motor: number;
  motor_current: number;
  motor_efficiency: number;
  motor_power_factor: number;
  motor_voltage: number;
  motorLoadRatioNP: number;
  motorLoadRatioRR: number;
  motorRatedRPM: number;
  baseWindingOperatingTemp: number;
  motorVoltageCorrections: MotorVoltageCorrections;
};

export type MotorCalculations = {
  motorVoltage60HzV: ComputedRef<number>;
  maxVSDOutputVoltage60HzV: ComputedRef<number>;
  motorPoles: ComputedRef<number>;
  totalRequiredBrakeHP: ComputedRef<number>;
  totalRequired60HzBrakeHP: ComputedRef<number>;
  reratedNameplateHP: ComputedRef<number>;
  nameplate50HzHP: ComputedRef<number>;
  reratedNameplateA: ComputedRef<number>;
  adjustedReratedNameplateA: ComputedRef<number>;
  adjustedReratedNameplateHP: ComputedRef<number>;
  adjustedReratedNameplateV: ComputedRef<number>;
  reratedNameplateV: ComputedRef<number>;
  nameplate50HzV: ComputedRef<number>;
  hpNameplate: ComputedRef<number>;
  hpElevationDerated: ComputedRef<number>;
  voltageNameplate: ComputedRef<number>;
  amperageNameplate: ComputedRef<number>;
  motorEnergyKWH: ComputedRef<number>;
  motorVoltageV: ComputedRef<number>;
  motorCurrentA: ComputedRef<number>;
  motorSkinTemperatureF: ComputedRef<number>;
  motorInternalTemperatureF: ComputedRef<number>;
  motorEfficiencyRatio: ComputedRef<number>;
  motorInputW: ComputedRef<number>;
  motorOutputW: ComputedRef<number>;
  motorLoadRatio: ComputedRef<number>;
  motorLoadRatioNP: ComputedRef<number>;
  motorPowerFactor: ComputedRef<number>;
  motorRatedRPM: ComputedRef<number>;
  flowVelocityAroundMotorFTSEC: ComputedRef<number>;
  bestMotorInternalTempF: ComputedRef<number>;
  motorShaftPowerLimitHP: ComputedRef<number>;
  motorShaftRatingRatio: ComputedRef<number>;
  wellFlowingPressureAtMotorPSIA: ComputedRef<number>;
  downholeMotorTemperatureF: ComputedRef<number>;
  yToolBypassTubingODAtMotorInches: ComputedRef<number>;
  yToolBypassTubingAreaAtMotorFt2: ComputedRef<number>;
  motorCrossSectionFt2: ComputedRef<number>;
  motorVoltageCorrections: ComputedRef<MotorVoltageCorrections>;
};

export type IndividualPumpCalculations = {
  [xrefPumpId: string]: {
    intakeStageCalculations: StageCalculations | undefined;
    dischargeStageCalculations: StageCalculations | undefined;
    stageCalculations: StageCalculations[];
    averageStageFlowRateBBLD?: number;
    efficiencyRatio: number;
    gasAtIntakeRatio: number;
    gasAtDischargeRatio: number;
    pumpGeneratedPressurePSIA: number;
    totalDynamicHeadFt: number;
    pumpGeneratedLiftHeadFt: number;
    gasLockRatio: number;
    shaftPowerLimitStandardHP: number;
    shaftPowerLimitHighStrengthHP: number;
    standardShaftRatingRatio: number;
    highStrengthShaftRatingRatio: number;
    operatingBurstRatioStandard: number;
    operatingBurstRatioHighStrength: number;
    shutInBurstRatioStandard: number;
    shutInBurstRatioHighStrength: number;
    shutInThrustLoad: number;
    totalBrakeHP: number;
    operatingThrustLoad: number;
    rorMin: number;
    rorMax: number;
    rorBep: number;
    graphMax: number;
    recRateCorrection: number;
    recHeadCorrection: number;
    recBHPCorrection: number;
    requiredStages: number;
    shutInPressurePSIA: number;
    liftPerStageHeadFt: number;
    bhpPerStageHP: number;
    xrefPump: XrefPumps[number];
    pumpIntakeFlowRateExceedsGraphMax: boolean;
    maxHeadFeet: number;
    maxHeadFlowRateBBLD: number;
    operatingHeadFeet: number;
    maxHousingPressurePSIA: number;
    mleMaxEllipseDepthIn: number;
    maxCalculatedOuterDiameterIn: number;
  };
};
export type AggregatePumpCalculations = {
  calculatedGasHeadRerateRatio: ComputedRef<number>;
  legacyGasHeadRerateRatio: ComputedRef<number>;
  averageLiquidDensityLBMFT3: ComputedRef<number>;
  averageFluidDensityLBMFT3: ComputedRef<number>;
  requiredPumpBrakeHP: ComputedRef<number>;
  averageSpecificGravity: ComputedRef<number>;
  averageLiquidSpecificGravity: ComputedRef<number>;
  requiredTotalDynamicHeadFt: ComputedRef<number>;
  producedTotalDynamicHeadFt: ComputedRef<number>;
  gasHeadRerateRatio: ComputedRef<number>;
  requiredDischargePressurePSIA: ComputedRef<number>;
  producedDischargePressurePSIA: ComputedRef<number>;
  differentialPressurePSIA: ComputedRef<number>;
  dischargeTemperatureF: ComputedRef<number>;
  operatingRPM: ComputedRef<number>;
  operatingThrustLoad: ComputedRef<number>;
  thrustLoadPounds: ComputedRef<number>;
  pumpEfficiencyRatio: ComputedRef<number>;
  shutInPressureRisePSIA: ComputedRef<number>;
  dischargeShutInBurstPressurePSIA: ComputedRef<number>;
  wellheadShutInBurstPressurePSIA: ComputedRef<number>;
  shutInThrustLoadPounds: ComputedRef<number>;
  stdShutInBurstPercentage: ComputedRef<number>;
  hsShutInBurstPercentage: ComputedRef<number>;
  operatingBurstPressurePsia: ComputedRef<number>;
  stdOperatingBurstPercentage: ComputedRef<number>;
  hsOperatingBurstPercentage: ComputedRef<number>;
  shaftPowerLimitStandardHP: ComputedRef<number>;
  shaftPowerLimitHighStrengthHP: ComputedRef<number>;
  intakeFlowRateSTBD: ComputedRef<number>;
  dischargeFlowRateSTBD: ComputedRef<number>;
  axialBoltLoadRatio: ComputedRef<number>;
  axialBoltLoadShutInRatio: ComputedRef<number>;
  wellFlowingPressureSurfacePSIA: ComputedRef<number>;
  pumpTorqueFtLbs: ComputedRef<number>;
  taperTotalOperatingHeadFeet: ComputedRef<number>;
  taperTotalMaxHeadFeet: ComputedRef<number>;
  totalMaxHeadFeet: ComputedRef<number>;
  totalMaxBrakeHP: ComputedRef<number>;
  totalMaxPressurePSIA: ComputedRef<number>;
  totalMaxDischargePressurePSIA: ComputedRef<number>;
};

export type IndividualIntakeCalculations = {
  [xrefIntakeId: string]: {
    shaftPowerLimitStandardHP: number;
    shaftPowerLimitHighStrengthHP: number;
    standardShaftRatingRatio: number;
    highStrengthShaftRatingRatio: number;
    title: string;
    maxCalculatedOuterDiameterIn: number;
    mleMaxEllipseDepthIn: number;
    xrefIntake: XrefIntakes[number];
  };
};
export type AggregateIntakeCalculations = {
  calculatedIntakeGasSeparationEfficiencyRatio: ComputedRef<number>;
  overrideIntakeGasSeparationEfficiencyRatio: ComputedRef<number | null>;
  intakeGasSeparationEfficiencyRatio: ComputedRef<number>;
  calculatedNaturalGasSeparationEfficiencyRatio: ComputedRef<number>;
  overrideNaturalGasSeparationEfficiencyRatio: ComputedRef<number | null>;
  naturalGasSeparationEfficiencyRatio: ComputedRef<number>;
  intakeHP: ComputedRef<number>;
  gasEnteringPumpRatio: ComputedRef<number>;
  turpinCoefficient: ComputedRef<number>;
  liquidAbovePumpMDFt: ComputedRef<number>;
  liquidAbovePumpTvdFt: ComputedRef<number>;
  intakesContainNonBoltOn: ComputedRef<boolean>;
};

export type IndividualCableCalculations = {
  [xrefCableId: string]: CableData & {
    xrefCable: XrefCables[number];
    downholeStartCableLengthFt: number;
    downholeEndCableLengthFt: number;
    cableMaxDepth: number;
    isSurface: boolean;
  };
};
export type AggregateCableCalculations = {
  totalImpedance: ComputedRef<number>;
  totalCablesCostPerMonthUSD: ComputedRef<number>;
  totalCablesVoltLossV: ComputedRef<number>;
  totalCablesPowerLossKW: ComputedRef<number>;
  totalCablesLengthFt: ComputedRef<number>;
  totalCablesWeightPounds: ComputedRef<number>;
  totalCablesAverageTemperatureF: ComputedRef<number>;
  totalCablesVoltLossAtNameplateAtMaxHzV: ComputedRef<number>;
  downholeTotalImpedance: ComputedRef<number>;
  downholeTotalCablesLengthFt: ComputedRef<number>;
  downholeTotalCablesAverageTemperatureF: ComputedRef<number>;
  downholeTotalCablesWeightPounds: ComputedRef<number>;
};

export type IndividualSealCalculations = {
  [xrefSealId: string]: {
    xrefSeal: XrefSeals[number];
    mleMaxEllipseDepthIn: number;
    maxCalculatedOuterDiameterIn: number;
    bearingRatingAt60hzLBS: number;
    bearingRatingAtRPMLBS: number;
    bearingLoadRatio: number;
    shutInBearingLoadRatio: number;
    shutInThrustLoadPounds: number;
    operatingHP: number;
    shaftPowerLimitStandardHP: number;
    shaftPowerLimitHighStrengthHP: number;
    standardShaftRatingRatio: number;
    highStrengthShaftRatingRatio: number;
    operationalTotalExpansionGal: number;
    pullTotalExpansionGal: number;
    operationalTotalAmountOfExpansionGal: number;
    pullTotalAmountOfExpansionGal: number;
    totalContractionVolumeExpansionGal: number;
    installExpansionRatio: number;
    operationExpansionRatio: number;
    pullExpansionRatio: number;
  };
};
export type AggregateSealCalculations = {
  sealOperatingHP: ComputedRef<number>;
  sealTotalVolumeGallons: ComputedRef<number>;
  sealUpperChamberPullExpansionRatio: ComputedRef<number>;
  sealBearingLoadRatio: ComputedRef<number>;
  sealShutInBearingLoadRatio: ComputedRef<number>;
  sealBearingRatingAt60HzLBS: ComputedRef<number>;
  sealBearingRatingAtRPMLBS: ComputedRef<number>;
  downholeSealTemperatureF: ComputedRef<number>;
  wellFlowingPressureAtSealPSIA: ComputedRef<number>;
};

export type MotorLeadExtensionCalculations = {
  mleCableData: ComputedRef<CableData>;
};

export type SurfaceDriveCalculations = {
  transformerTapData: ComputedRef<TransformerTapData>;
  surfaceDriveMaxHz: ComputedRef<number>;
  surfaceDriveMinHz: ComputedRef<number>;
  requiredVoltageAtMaxHzV: ComputedRef<number>;
  requiredVoltageAtMaxHz60HzV: ComputedRef<number>;
  kvaAtMaxHz: ComputedRef<number>;
  iLimitA: ComputedRef<number>;
  iLimitSync: ComputedRef<number>;
  vsdUnderloadA: ComputedRef<number>;
  vsdOverloadA: ComputedRef<number>;
  startupVoltage: ComputedRef<number>;
  startUpVoltageNamplateRatio: ComputedRef<number>;
  monthlySystemCostUSD: ComputedRef<number>;
  requiredVoltageV: ComputedRef<number>;
  requiredKVA: ComputedRef<number>;
  stepDownTransformerLoadRatio: ComputedRef<number>;
  stepUpTransformerLoadRatio: ComputedRef<number>;
  vsdLoadRatio: ComputedRef<number>;
  systemEmissionsCO2T: ComputedRef<number>;
  requiredKVA60Hz: ComputedRef<number>;
  maxAmpRatingA: ComputedRef<number>;
  surfacePFRatio: ComputedRef<number>;
  systemPowerKW: ComputedRef<number>;
  vfdLoadRatio: ComputedRef<number>;
  vsdKVARating: ComputedRef<number>;
  transformerRatio: ComputedRef<number>;
  vsdOutputVoltageV: ComputedRef<number>;
  totalSystemEfficiencyRatio: ComputedRef<number>;
  cableEfficiencyRatio: ComputedRef<number>;
  sealEfficiencyRatio: ComputedRef<number>;
  pumpEfficiencyRatio: ComputedRef<number>;
  motorEfficiencyRatio: ComputedRef<number>;
};

export type FluidPropertiesCalculations = {
  gasSpecificGravity: ComputedRef<number>;
  fluidPropertiesAtDatum: ComputedRef<FluidProperties>;
  fluidPropertiesAtReservoir: ComputedRef<FluidProperties>;
  fluidPropertiesAtWellPerfs: ComputedRef<FluidProperties>;
  fluidPropertiesAtMotor: ComputedRef<FluidProperties>;
  fluidPropertiesAtSeals: ComputedRef<FluidProperties>;
  fluidPropertiesAtSensor: ComputedRef<FluidProperties | undefined>;
  fluidPropertiesAtIntake: ComputedRef<FluidProperties>;
  fluidPropertiesAtPumpIntake: ComputedRef<FluidProperties>;
  fluidPropertiesAfterAllGasSeparationAtPump: ComputedRef<FluidProperties>;
  oilPhaseGasSolubilityAtBubblePointSCFSTB: ComputedRef<number>;
  hpsStageFluidProperties: ComputedRef<HPSFluidPropertiesPipelineResults>;
};

export type PumpWithHousingIndex = {
  pump: XrefPump;
  housing?: XrefHousing;
  housingIndex: number;
};

export type SealBearingDataForPumpCalculations = SealData & {
  sealODIn: number;
  sealThrustCollarODIn: number;
  sealThrustCollarIDIn: number;
  sealWeightLbs: number;
};

export type ScenarioCalculations = {
  liquidDensityAtDatumLbmft3: ComputedRef<number>;
  waterCutRatio: ComputedRef<number>;
  tempGrad: ComputedRef<number>;
  orderedIntakes: ComputedRef<XrefIntakes>;
  descendingOrderPumps: ComputedRef<XrefPumps>;
  ascendingOrderPumps: ComputedRef<XrefPumps>;
  primaryPump: ComputedRef<Maybe<XrefPumps[number]>>;
  bottomPump: ComputedRef<Maybe<XrefPumps[number]>>;
  primaryIntake: ComputedRef<Maybe<XrefIntakes[number]>>;
  bottomIntake: ComputedRef<Maybe<XrefIntakes[number]>>;
  intakesContainGasSeparator: ComputedRef<boolean>;
  primarySeal: ComputedRef<Maybe<XrefSeals[number]>>;
  bottomSeal: ComputedRef<Maybe<XrefSeals[number]>>;
  orderedHousings: ComputedRef<XrefHousings>;
  descendingPumpsWithHousingIndex: ComputedRef<PumpWithHousingIndex[]>;
  orderedCables: ComputedRef<XrefCables>;
  descendingOrderSeals: ComputedRef<XrefSeals>;
  yToolBypassTubingMaxODIn: ComputedRef<number>;
  orderedYToolBypassTubings: ComputedRef<XrefYToolBypassTubings>;
  orderedYToolEquipmentTubings: ComputedRef<XrefYToolEquipmentTubings>;
  productivityIndexStbdPsia: WritableComputedRef<number>;
  equipmentLengths: EquipmentLengths;
  equipmentWeights: EquipmentWeights;
  motorLeadExtensionCalculations: MotorLeadExtensionCalculations;
  surfaceDriveCalculations: SurfaceDriveCalculations;
  motorCalculations: MotorCalculations;
  pumpCalculations: {
    individual: ComputedRef<IndividualPumpCalculations>;
    aggregate: AggregatePumpCalculations;
  };
  cableCalculations: {
    individual: ComputedRef<IndividualCableCalculations>;
    aggregate: AggregateCableCalculations;
  };
  sealCalculations: {
    individual: ComputedRef<IndividualSealCalculations>;
    aggregate: AggregateSealCalculations;
  };
  intakeCalculations: {
    individual: ComputedRef<IndividualIntakeCalculations>;
    aggregate: AggregateIntakeCalculations;
  };
  fluidPropertiesAtReservoirContext: ComputedRef<
    FluidPropertiesContext | undefined
  >;
  fluidPropertiesInsideTubingContext: ComputedRef<
    FluidPropertiesContext | undefined
  >;
  fluidPropertiesAfterPumpDischargeTubingContext: ComputedRef<
    FluidPropertiesContext | undefined
  >;
  fluidProperties: FluidPropertiesCalculations;
  topTubingFrictionProperties: ComputedRef<PipeFrictionProperties>;
  bottomTubingFrictionProperties: ComputedRef<PipeFrictionProperties>;
  entryRows: ComputedRef<DeviationSurveyEntryRows>;
  entryRowsSurfaceToBoe: ComputedRef<number[][]>;
  entryRowsBotToBoe: ComputedRef<number[][]>;
  entryRowsAtSeals: ComputedRef<number[][]>;
  basicData: ComputedRef<BasicData>;
  surfaceTemperatureF: ComputedRef<number>;
  bottomholeTemperatureF: ComputedRef<number>;
  surfaceFlowRates: ComputedRef<FlowRates>;
  wellTestFlowRates: ComputedRef<FlowRates>;
  pumpHz: ComputedRef<number>;
  casingFlowRegime: ComputedRef<string>;
  adjustedPumpHzFrequency: ComputedRef<number>;
  dischargeFluidDensityLBMFT3: ComputedRef<number>;
  desiredLiquidRateSTDB: ComputedRef<number>;
  pumpIntakeTemperatureF: ComputedRef<number>;
  pumpAverageTemperatureF: ComputedRef<number>;
  pumpIntakePressurePSIA: ComputedRef<number>;
  intakePressurePSIA: ComputedRef<number>;
  wellFlowingPressureAtSensorPSIA: ComputedRef<number | undefined>;
  wellFlowingPressureAtDatumPSIA: ComputedRef<number>;
  wellFlowingPressureAtPerfsPSIA: ComputedRef<number>;
  wellFlowingPressureSurfacePSIA: ComputedRef<number>;
  maxFlowRateBBLD: ComputedRef<number>;
  maxFlowRateAtIntakeBBLD: ComputedRef<number>;
  averageViscosityCP: ComputedRef<number>;
  hasDeviationSurvey: ComputedRef<boolean>;
  selectedBubblePointPressurePSIA: ComputedRef<number>;
  selectedDewPointPressurePSIA: ComputedRef<number>;
  selectedFluidViscosityCP: ComputedRef<number>;
  selectedNPSHaFt: ComputedRef<number>;
  downholeSensorTemperatureF: ComputedRef<number | undefined>;
  downholeMotorTemperatureF: ComputedRef<number>;
  downholeWellPerfsTemperatureF: ComputedRef<number>;
  downholeTubingTemperatureF: ComputedRef<number>;
  tubingFrictionLossPSIA: ComputedRef<number>;
  casingFrictionLossPSIA: ComputedRef<number>;
  perfsAndDatumCasingFrictionPSIA: ComputedRef<number>;
  bottomOfEquipmentCasingFrictionPSIA: ComputedRef<number>;
  sensorAndMotorCasingFrictionPSIA: ComputedRef<number>;
  motorAndSealsCasingFrictionPSIA: ComputedRef<number>;
  sealsAndIntakesCasingFrictionPSIA: ComputedRef<number>;
  tubingInternalDiameterAtPumpDischargeIn: ComputedRef<number>;
  tubingOuterDiameterAtPumpDischargeIn: ComputedRef<number>;
  destructuredScenario: DeepMaybeRef<DestructuredScenario>;
  customFluidProperties: ComputedRef<LagrangeInterpolationMapWithGroupings | null>;
  customViscosityProperties: ComputedRef<LagrangeInterpolationMapWithGroupings | null>;
  isScenarioEspShore: ComputedRef<boolean>;
  stageFluidProperties?: ComputedRef<FluidPropertiesPipelineResults>;
};
